import {
  autoUpdate,
  flip,
  FloatingNode,
  FloatingTree,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useFloatingParentNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import type { LucideIcon } from 'lucide-react';
import { Check, ChevronDown, MoreVertical } from 'lucide-react';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useId, useState } from 'react';

import { useEditorChrome } from '../core/context/EditorChromeContext';
import { EditorPortal } from './EditorPortal';
import { keepSelection, panelClasses, toolbarButtonClasses, toolbarIconClasses } from './styles';

export type ToolbarMenuProps = {
  icon: LucideIcon;
  label: string;
  active?: boolean;
  disabled?: boolean;
  /** `more` shows Froala's "⋮", `chevron` its dropdown arrow, `none` neither (a plain popup). */
  caret?: 'chevron' | 'more' | 'none';
  /** `list` stacks menu items; `row` lays toolbar buttons out like a mini toolbar; `fit` hugs custom content. */
  layout?: 'list' | 'row' | 'fit';
  /** Smaller trigger for in-content use (e.g. the table cell menu). */
  compact?: boolean;
  /** Controlled mode, for opening the menu from elsewhere (e.g. a right click). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Rendered inside the dropdown panel; receives a `close` callback. */
  children: (close: () => void) => ReactNode;
};

/**
 * A toolbar button that opens a dropdown panel, matching Froala's `fr-dropdown`.
 * - The panel renders in an `EditorPortal`, and holds the editor focused while open.
 * - Menus nest (e.g. table picker inside the "more" popup) via a shared `FloatingTree`.
 */
export const ToolbarMenu = (props: ToolbarMenuProps) => {
  const parentId = useFloatingParentNodeId();

  // Only the outermost menu owns the tree, so clicks in a child panel don't dismiss the parent
  if (parentId === null) {
    return (
      <FloatingTree>
        <ToolbarMenuNode {...props} />
      </FloatingTree>
    );
  }
  return <ToolbarMenuNode {...props} />;
};

const ToolbarMenuNode = ({
  icon: Icon,
  label,
  active = false,
  disabled = false,
  caret = 'chevron',
  layout = 'list',
  compact = false,
  open: controlledOpen,
  onOpenChange,
  children,
}: ToolbarMenuProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = useCallback(
    (next: boolean | ((current: boolean) => boolean)) => {
      const value = typeof next === 'function' ? next(open) : next;
      setUncontrolledOpen(value);
      onOpenChange?.(value);
    },
    [onOpenChange, open],
  );
  const nodeId = useFloatingNodeId();
  const id = useId();
  const { retainFocus } = useEditorChrome();

  const { refs, floatingStyles, context } = useFloating({
    nodeId,
    open,
    onOpenChange: setOpen,
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([useDismiss(context), useRole(context, { role: 'menu' })]);

  useEffect(() => {
    retainFocus(id, open);
    return () => retainFocus(id, false);
  }, [id, open, retainFocus]);

  const close = useCallback(() => setOpen(false), [setOpen]);

  return (
    <FloatingNode id={nodeId}>
      <button
        type="button"
        title={label}
        aria-label={label}
        aria-expanded={open}
        disabled={disabled}
        ref={refs.setReference}
        className={
          compact
            ? `border-greyscale-15 flex size-5 cursor-pointer items-center justify-center rounded border bg-white shadow-sm ${open ? 'text-primary-100' : 'text-black'}`
            : toolbarButtonClasses(active || open, disabled)
        }
        onMouseDown={keepSelection}
        onClick={() => !disabled && setOpen((v) => !v)}
        {...getReferenceProps()}
      >
        <Icon className={compact ? 'size-3.5' : toolbarIconClasses} strokeWidth={compact ? 2.25 : 1.75} aria-hidden />
        {caret === 'more' && <MoreVertical className="-ml-0.5 size-3" strokeWidth={2} aria-hidden />}
        {caret === 'chevron' && <ChevronDown className="-ml-0.5 size-3" strokeWidth={2.5} aria-hidden />}
      </button>

      {open && (
        <EditorPortal
          ref={refs.setFloating}
          style={floatingStyles}
          className={`${panelClasses} ${{ list: 'min-w-44 py-1', row: 'flex items-center gap-0.5 p-1', fit: '' }[layout]}`}
          {...getFloatingProps()}
        >
          {children(close)}
        </EditorPortal>
      )}
    </FloatingNode>
  );
};

export type ToolbarMenuItemProps = {
  label: string;
  icon?: LucideIcon;
  active?: boolean;
  /** On/off options: set to show a check on the right instead of the `active` tint. */
  checked?: boolean;
  disabled?: boolean;
  onClick: () => void;
};

export const ToolbarMenuItem = ({ label, icon: Icon, active = false, checked, disabled = false, onClick }: ToolbarMenuItemProps) => (
  <button
    type="button"
    role={checked === undefined ? 'menuitem' : 'menuitemcheckbox'}
    aria-checked={checked}
    disabled={disabled}
    className={`flex w-full items-center gap-2 px-3 py-1.5 text-left ${
      disabled ? 'cursor-not-allowed opacity-40' : 'hover:bg-background-mid-1 cursor-pointer'
    } ${active ? 'text-primary-100 bg-background-mid-1' : 'text-black'}`}
    onMouseDown={keepSelection}
    onClick={() => !disabled && onClick()}
  >
    {Icon && <Icon className="size-[18px] shrink-0" strokeWidth={1.75} aria-hidden />}
    <span className="text-pbk-14">{label}</span>
    {checked !== undefined && (
      <Check className={`text-semantic-success ml-auto size-4 shrink-0 ${checked ? '' : 'invisible'}`} strokeWidth={2.5} aria-hidden />
    )}
  </button>
);

/** Section heading inside a list menu. */
export const ToolbarMenuLabel = ({ children }: { children: ReactNode }) => (
  <div className="text-pbk-12 text-text-2 px-3 pt-2 pb-0.5 font-medium">{children}</div>
);

export const ToolbarMenuSeparator = () => <div className="bg-greyscale-15 my-1 h-px" aria-hidden />;
